/**
 * Fn 层出厂键的解析、匹配与展示改写。
 *
 * 出厂定义就是 keycode.json 里的 keywords；这里把它解析成 type/code 后再与固件回读值比对，
 * 而不是反过来拿回读值去反查 keywords —— 后者在一个 keycode 条目写错时会静默失配。
 *
 * Win / Mac 层使用不同的 factoryKeys 表，由 pickFactoryKeyMap 按当前层选取。
 */
import type { keyMapItem, KeysCapability } from '@rapoo/device-sdk'
import { parseNumber } from './keyIdentity'
import i18n from '@/locales'
import { KEYCODE_KEYWORDS_MAP, getLocalizedField } from './keycodeMap'
import type { KeyDisplayOverride, KeyPlatform, ResolvedFactoryKey } from '../types'

/**
 * 按当前 Win/Mac 层取出对应的 Fn 出厂键表。
 * Mac 表缺失时回退到 Win，避免无 Mac 配置的机型读空。
 */
export const pickFactoryKeyMap = (
  factoryKeys: KeysCapability['config']['factoryKeys'] | undefined,
  platform: KeyPlatform,
): Record<string, string> => {
  if (!factoryKeys) return {}
  return factoryKeys[platform] ?? factoryKeys.win ?? {}
}

/** 固件回读的 code 为数组，统一取首位 */
export const readMappingCode = (item: { keyAttr?: { code?: unknown } }): number | undefined => {
  const code = item.keyAttr?.code
  return Array.isArray(code) ? parseNumber(code[0]) : parseNumber(code)
}

/**
 * 把出厂功能 keywords 解析成可直接比对与下发的 type/code。
 *
 * @param keywords 机型配置中的出厂功能 keywords
 * @returns 解析结果；keycode 表中查不到或缺 type/code 时返回 undefined
 */
export const resolveFactoryKey = (keywords?: string): ResolvedFactoryKey | undefined => {
  if (!keywords) return undefined

  const meta = KEYCODE_KEYWORDS_MAP.get(keywords)
  if (!meta) return undefined

  const type = parseNumber(meta.type)
  const code = parseNumber(meta.code)
  if (type == null || code == null) return undefined

  return {
    keywords,
    type,
    code,
    confIndex: parseNumber((meta as { confIndex?: unknown }).confIndex) ?? undefined,
  }
}

/**
 * 判断固件回读项是否仍等于出厂定义。
 *
 * confIndex 仅在出厂键本身带该字段（配置键）时才参与比对；
 * 灯效等 type9 功能键忽略回读里的 confIndex。
 */
export const isMappingMatchFactory = (item: keyMapItem, resolved?: ResolvedFactoryKey): boolean => {
  if (!resolved) return false

  const type = parseNumber(item.keyAttr?.type)
  const code = readMappingCode(item)
  if (type == null || code == null) return false
  if (type !== resolved.type || code !== resolved.code) return false

  if (resolved.confIndex != null) {
    return resolved.confIndex === item.keyAttr?.confIndex
  }

  return true
}

/** 出厂功能在 UI 上的展示名 */
export const getFactoryKeyLabel = (keywords?: string): string => {
  if (!keywords) return ''
  const meta = KEYCODE_KEYWORDS_MAP.get(keywords)
  return getLocalizedField(meta?.alias || meta?.name) || keywords
}

/**
 * 查找命中的展示改写规则。
 * 规则写了 keywords 就按 keywords 匹配，否则按 type/code 匹配。
 */
export const matchDisplayOverride = (
  overrides: KeyDisplayOverride[] | undefined,
  target: { keywords?: string; type?: number; code?: number },
): KeyDisplayOverride | undefined => {
  if (!overrides?.length) return undefined

  return overrides.find((override) => {
    if (override.keywords) {
      return Boolean(target.keywords) && override.keywords === target.keywords
    }
    if (override.type == null || override.code == null) return false
    return override.type === target.type && override.code === target.code
  })
}

/** 展示改写后的文案；规则未配文案时返回 undefined */
export const resolveOverrideLabel = (override?: KeyDisplayOverride): string | undefined => {
  if (!override) return undefined
  if (override.labelKey) return i18n.global.t(override.labelKey) as string
  return override.label || undefined
}
